import {
  ElementType,
  FixtureType,
  LineType,
  SymbolType,
} from '@ritabygglov/core';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { useAppState } from '../../context/AppState/useAppState';
import { useElementTransformation } from '../../context/MouseEvents/hooks/useElementTransformation';
import { PresetType, SelectionModeHelper, SelectionModes } from '../../types';
import { Flex } from '../Flex/Flex';
import { Sizing } from '../../styles/spacing';
import { Text } from '../Text/Text';
import { FontSize } from '../../styles/typography';
import {
  Dimension,
  FixtureToolbarIcon,
  HandIcon,
  LineSvg,
  PlusIcon,
  PointerSvg,
  RectSvg,
  TextIcon,
  RightAngleIcon,
  AngleIcon,
} from './SvgIcons';

import { CheckMark } from '../Icons';
import { fixtureRegistry } from '../../services/fixtureRegistry';
import { presetRegistry } from '../../services/presetRegistry';
import { symbolRegistry } from '../../services/symbolRegistry';

const ACTIVE_COLOR = 'white';
const INACTIVE_COLOR = '#152437';

const ContextMessageContainer = styled.div`
  position: absolute;
  top: 50px;
  left: 50%;
  z-index: 2;
  transform: translate(-50%, 0);
  padding: 8px;
  background-color: rgb(245, 244, 145);
  box-shadow: 0 0 8px rgba(255, 255, 255, 0.4);
  border: solid 1px #eae815;
`;

const ContextButtonContainer = styled.div`
  position: absolute;
  display: flex;
  flex-direction: row;
  bottom: 0px;
  left: 50%;
  transform: translate(-50%, 0);
  z-index: 2;
  background-color: var(--surface);
  border-radius: 4px 4px 0 0;
  border: 1px solid var(--border);
  padding: 0.5rem 1rem;
  box-shadow: 0px -2px 4px 0px rgba(0, 0, 0, 0.08);
`;

const ContextButton = styled.button`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  background-color: var(--surface-elevated);
  border: 1px solid var(--border);

  box-shadow:
    0px 1px 6px 0 rgba(0, 0, 0, 0.06),
    0px 1px 0 0 var(--border-light);

  font-weight: 500;
  font-size: 0.8rem;
  cursor: pointer;
  border-radius: 4px;
  padding: 0.2rem 0.4rem;
  gap: 8px;
  margin: 0 2px;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  white-space: nowrap;
  &:hover {
    background-color: #fff;
    border: solid 1px var(--border-dark);
  }
`;

const ContextToggleButton = styled(ContextButton)<{ $isActive: boolean }>`
  background-color: ${(props) =>
    props.$isActive ? 'var(--hover-blue)' : 'var(--surface-elevated)'};
  color: ${(props) => (props.$isActive ? 'var(--active-blue)' : 'var(--text)')};
  border-color: ${(props) =>
    props.$isActive ? 'var(--active-blue)' : 'var(--border)'};

  &:hover {
    border-color: ${(props) =>
      props.$isActive ? 'var(--active-blue)' : 'var(--border-dark)'};
  }
`;

const HotkeyIndicator = styled.div`
  padding: 0;
  color: var(--text-muted);
  font-weight: 600;
  font-size: 0.6rem;
`;

const ContextMessage = styled.div`
  color: rgba(21, 36, 55, 1);
  font-size: 0.8rem;
  font-weight: 600;
`;

const Toolbar = styled.div`
  position: absolute;
  top: 49px;
  left: 0px;
  bottom: 0;
  z-index: 3;
  background-color: var(--surface);
  border: 1px solid var(--border);
  padding: 1.5rem 0.25rem;
  box-sizing: border-box;
`;

const Button = styled.button<{ $isActive: boolean }>`
  border: solid 1px var(--surface);
  color: ${(props) => (props.$isActive ? 'white' : 'var(--dark-blue)')};
  background-color: ${(props) =>
    props.$isActive ? 'var(--active-blue)' : 'var(--surface)'};
  border-radius: 4px;
  padding: 0.5rem 0.25rem;
  text-align: center;
  letter-spacing: -4%;
  width: 60px;
  height: 60px;
  font-size: 10px;
  &:hover {
    background-color: ${(props) =>
      props.$isActive ? 'rgba(4, 109, 243, 1)' : 'var(--surface-elevated)'};
    border: solid 1px var(--border-light);
  }
`;

const PanelOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9;
`;

const Panel = styled.div`
  position: fixed;
  top: 49px;
  left: 68px;
  bottom: 0;
  width: 320px;
  background: var(--surface);
  border: 1px solid var(--border);
  box-shadow: 2px 0 4px rgba(0, 0, 0, 0.06);
  z-index: 10;
  overflow-y: auto;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 0.5rem;
  border-bottom: 1px solid var(--border);
`;

const PanelCloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem 1rem;
  color: var(--dark-blue);
  font-size: 18px;
  border: solid 1px var(--surface);
  &:hover {
    background: var(--surface-elevated);
    border: solid 1px var(--border);
  }
`;

const PanelGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
  padding: 0.2rem;
`;

const PanelItem = styled.div<{ $isActive?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-height: 56px;
  padding: 4px 4px;
  border-radius: 4px;
  cursor: pointer;
  color: ${(props) => (props.$isActive ? 'white' : 'var(--dark-blue)')};
  background-color: ${(props) =>
    props.$isActive ? 'var(--active-blue)' : 'var(--surface)'};
  border: solid 1px var(--surface);
  &:hover {
    background-color: ${(props) =>
      props.$isActive ? 'var(--active-blue)' : '#fff'};
    border: solid 1px var(--border-light);

    & svg {
      opacity: 1;
    }
  }

  & svg {
    width: 24px;
    height: 24px;

    opacity: 0.8;
  }
`;

type DropdownValue = {
  isActive: boolean;
  icon: React.ReactElement;
  text: string;
  symbolType?: SymbolType;
  fixtureType?: FixtureType;
  testId?: string;
  onClick: () => void;
};

export const BottomDropdown = ({ values }: { values: DropdownValue[] }) => {
  const [open, setOpen] = useState(false);
  const intl = useIntl();

  useEffect(() => {
    if (!open) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open]);

  return (
    <>
      {open && (
        <>
          <PanelOverlay onClick={() => setOpen(false)} />
          <Panel>
            <PanelHeader>
              <PanelCloseButton onClick={() => setOpen(false)}>
                ✕
              </PanelCloseButton>
            </PanelHeader>
            <PanelGrid>
              {values.map((item) => (
                <PanelItem
                  key={item.text}
                  $isActive={item.isActive}
                  data-testid={
                    item.testId ??
                    `${item.symbolType ? 'symbol' : 'fixture'}-${(item.symbolType || item.fixtureType)?.toLowerCase()}-btn`
                  }
                  onClick={() => {
                    item.onClick();
                    setOpen(false);
                  }}
                >
                  {item.icon}
                  <Text
                    $fontSize={FontSize.md}
                    $textAlign="center"
                    $color={item.isActive ? ACTIVE_COLOR : INACTIVE_COLOR}
                  >
                    {item.text}
                  </Text>
                </PanelItem>
              ))}
            </PanelGrid>
          </Panel>
        </>
      )}
      <Button $isActive={open} onClick={() => setOpen(!open)}>
        <PlusIcon isFill={open} color={open ? ACTIVE_COLOR : INACTIVE_COLOR} />
        <Text
          $fontSize={FontSize.sm}
          $color={open ? ACTIVE_COLOR : INACTIVE_COLOR}
        >
          {intl.formatMessage({ id: 'toolbar.addElement' })}
        </Text>
      </Button>
    </>
  );
};
const ToolbarComponent = () => {
  const hooks = useAppState();
  const intl = useIntl();
  const {
    appState,
    selectedElements,
    setSelectedElements,
    selectionMode,
    setSelectionMode,
    isSpacePressed,
    perpendicularGuideEnabled,
    setPerpendicularGuideEnabled,
    showTriangleAngles,
    setShowTriangleAngles,
  } = hooks;
  const { onStopPolyline } = useElementTransformation(hooks);

  const symbolType = selectionMode.symbolType;
  const fixtureType = selectionMode.fixtureType;
  const presetType = selectionMode.presetType;
  const isPanning = SelectionModeHelper.isPanning({
    selectionMode,
    isSpacePressed,
  });
  const isActiveSelect =
    (SelectionModeHelper.isNone(selectionMode) ||
      SelectionModeHelper.isMultiSelecting(selectionMode) ||
      SelectionModeHelper.isSelected(selectionMode)) &&
    !isSpacePressed;
  const isAddingRect =
    SelectionModeHelper.isAddingRect(selectionMode) && !isSpacePressed;
  const isAddingText =
    SelectionModeHelper.isAddingText(selectionMode) && !isSpacePressed;
  const isAddingPolyline =
    SelectionModeHelper.isAddingPolyline(selectionMode) &&
    !SelectionModeHelper.isAddingDimension(selectionMode) &&
    !isSpacePressed;
  const isActiveDimension =
    SelectionModeHelper.isAddingDimension(selectionMode) && !isSpacePressed;

  const isPolylineActive = SelectionModeHelper.isPolylineActive(selectionMode);
  const isTextEditing = SelectionModeHelper.isTextEditing(selectionMode);
  const isTextActive =
    SelectionModeHelper.isTextActive(selectionMode) &&
    !SelectionModeHelper.isAddingText(selectionMode) &&
    !isTextEditing;

  const contextMessageText = () => {
    const element = appState.elements[selectedElements[0]];
    const isPolyline = element?.type === ElementType.Polyline;

    if (isSpacePressed) {
      return intl.formatMessage({ id: 'toolbar.contextPanSpace' });
    } else if (isPanning) {
      return intl.formatMessage({ id: 'toolbar.contextPanTip' });
    } else if (isAddingRect) {
      return intl.formatMessage({ id: 'toolbar.contextAddRect' });
    } else if (isAddingText) {
      return intl.formatMessage({ id: 'toolbar.contextAddText' });
    } else if (isAddingPolyline) {
      if (isPolyline && element.points.length > 1) {
        return intl.formatMessage({ id: 'toolbar.contextAddPolyline' });
      }
      return intl.formatMessage({ id: 'toolbar.contextAddPolylineFirst' });
    } else if (isActiveDimension) {
      if (isPolyline && element.points.length > 1) {
        return intl.formatMessage({ id: 'toolbar.contextAddPolyline' });
      }
      return intl.formatMessage({ id: 'toolbar.contextAddDimensionFirst' });
    }
    if (isPolylineActive) {
      return intl.formatMessage({ id: 'toolbar.contextEditPolyline' });
    } else if (isTextEditing) {
      return intl.formatMessage({ id: 'toolbar.contextEditingText' });
    } else if (isTextActive) {
      return intl.formatMessage({ id: 'toolbar.contextEditText' });
    } else if (isActiveSelect) {
      return intl.formatMessage({ id: 'toolbar.contextSelect' });
    }
    return '';
  };

  const activeColor = 'white';
  const inactiveColor = '#152437';

  const isAddingTriangle =
    SelectionModeHelper.isAddingTriangle(selectionMode) && !isSpacePressed;
  const isAddingTrapezoid =
    SelectionModeHelper.isAddingTrapezoid(selectionMode) && !isSpacePressed;
  const isAddingAngle =
    SelectionModeHelper.isAddingAngle(selectionMode) && !isSpacePressed;
  const symbolValues = [
    {
      isActive: isAddingTriangle,
      icon: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <polygon
            points="8,1 15,15 1,15"
            stroke={isAddingTriangle ? 'white' : '#152437'}
            strokeWidth="1.5"
            fill="none"
          />
        </svg>
      ),
      text: intl.formatMessage({ id: 'toolbar.symbolTriangle' }),
      symbolType: SymbolType.GENERAL_WELL,
      onClick: () => {
        setSelectedElements([]);
        setSelectionMode({
          type: SelectionModes.Add,
          elementType: ElementType.Triangle,
        });
      },
    },
    {
      isActive: isAddingTrapezoid,
      icon: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <polygon
            points="4,2 12,2 15,14 1,14"
            stroke={isAddingTrapezoid ? 'white' : '#152437'}
            strokeWidth="1.5"
            fill="none"
          />
        </svg>
      ),
      text: intl.formatMessage({ id: 'toolbar.symbolTrapezoid' }),
      symbolType: SymbolType.WELL,
      onClick: () => {
        setSelectedElements([]);
        setSelectionMode({
          type: SelectionModes.Add,
          elementType: ElementType.Trapezoid,
        });
      },
    },
    {
      isActive: isAddingAngle,
      icon: (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <polyline
            points="1,15 8,1 15,15"
            stroke={isAddingAngle ? 'white' : '#152437'}
            strokeWidth="1.5"
            fill="none"
          />
        </svg>
      ),
      text: intl.formatMessage({ id: 'toolbar.symbolAngle' }),
      testId: 'symbol-angle-btn',
      onClick: () => {
        setSelectedElements([]);
        setSelectionMode({
          type: SelectionModes.Add,
          elementType: ElementType.Angle,
        });
      },
    },
    ...(Object.keys(symbolRegistry) as SymbolType[]).map((st) => {
      const def = symbolRegistry[st];
      const isActive =
        selectionMode.type === SelectionModes.Add && symbolType === st;
      const ToolbarIcon = def.toolbarIcon;
      return {
        isActive,
        icon: <ToolbarIcon isFill={isActive} />,
        text: intl.formatMessage({ id: def.i18nKey }),
        symbolType: st,
        onClick: () => {
          setSelectedElements([]);
          setSelectionMode({
            type: SelectionModes.Add,
            elementType: ElementType.Symbol,
            symbolType: st,
          });
        },
      };
    }),
    ...(Object.keys(fixtureRegistry) as FixtureType[]).map((ft) => {
      const def = fixtureRegistry[ft];
      const isActive =
        selectionMode.type === SelectionModes.Add && fixtureType === ft;
      return {
        isActive,
        icon: (
          <FixtureToolbarIcon
            isFill={isActive}
            component={def.icon}
            viewBoxWidth={def.toolbarViewBoxWidth}
            viewBoxHeight={def.toolbarViewBoxHeight}
            toolbarId={def.toolbarId}
          />
        ),
        text: intl.formatMessage({ id: def.i18nKey }),
        fixtureType: ft,
        onClick: () => {
          setSelectedElements([]);
          setSelectionMode({
            type: SelectionModes.Add,
            elementType: ElementType.Fixture,
            fixtureType: ft,
          });
        },
      };
    }),
    ...(Object.keys(presetRegistry) as PresetType[]).map((pt) => {
      const def = presetRegistry[pt];
      const isActive =
        selectionMode.type === SelectionModes.Add && presetType === pt;
      return {
        isActive,
        icon: (
          <FixtureToolbarIcon
            isFill={isActive}
            component={def.icon}
            viewBoxWidth={def.toolbarViewBoxWidth}
            viewBoxHeight={def.toolbarViewBoxHeight}
            toolbarId={def.toolbarId}
          />
        ),
        text: intl.formatMessage({ id: def.i18nKey }),
        testId: `preset-${pt.toLowerCase()}-btn`,
        onClick: () => {
          setSelectedElements([]);
          setSelectionMode({
            type: SelectionModes.Add,
            elementType: ElementType.Group,
            presetType: pt,
          });
        },
      };
    }),
  ];

  const onStopAddingPolyline = (shouldRemoveLastPoint: boolean = true) => {
    onStopPolyline(shouldRemoveLastPoint);
  };

  const renderContextButton = () => {
    const element = appState.elements[selectedElements[0]];
    const isPolyline = element?.type === ElementType.Polyline;
    const showEndLine =
      isAddingPolyline && isPolyline && element.points.length > 1;
    const isDimensionLineSelected = selectedElements.some((id) => {
      const el = appState.elements[id];
      return (
        el?.type === ElementType.Polyline && el.lineType === LineType.Dimension
      );
    });
    const showPerpendicularToggle =
      isActiveDimension || isDimensionLineSelected;
    const showAnglesToggle = selectedElements.some((id) => {
      const type = appState.elements[id]?.type;
      return type === ElementType.Triangle || type === ElementType.Angle;
    });

    if (!showEndLine && !showPerpendicularToggle && !showAnglesToggle)
      return null;

    return (
      <ContextButtonContainer>
        {showEndLine && (
          <ContextButton onClick={() => onStopAddingPolyline()}>
            <CheckMark fill="var(--text-muted)" size="16" />
            <Flex
              $alignItems="start"
              $flexDirection="column"
              $gap={Sizing.none}
            >
              {intl.formatMessage({ id: 'toolbar.endLine' })}
              <HotkeyIndicator>(enter)</HotkeyIndicator>
            </Flex>
          </ContextButton>
        )}
        {showPerpendicularToggle && (
          <ContextToggleButton
            $isActive={perpendicularGuideEnabled}
            data-testid="perpendicular-guide-toggle"
            onClick={() => setPerpendicularGuideEnabled((prev) => !prev)}
          >
            <RightAngleIcon
              color={
                perpendicularGuideEnabled
                  ? 'var(--active-blue)'
                  : INACTIVE_COLOR
              }
              isFill={perpendicularGuideEnabled}
            />
            {/*            <Dimension
              color={
                perpendicularGuideEnabled
                  ? 'var(--active-blue)'
                  : INACTIVE_COLOR
              }
              isFill={perpendicularGuideEnabled}
            />*/}
            {intl.formatMessage({ id: 'toolbar.perpendicularGuide' })}
          </ContextToggleButton>
        )}
        {showAnglesToggle && (
          <ContextToggleButton
            $isActive={showTriangleAngles}
            data-testid="triangle-angles-toggle"
            onClick={() => setShowTriangleAngles((prev) => !prev)}
          >
            <AngleIcon
              color={showTriangleAngles ? 'var(--active-blue)' : INACTIVE_COLOR}
              isFill={showTriangleAngles}
            />
            {intl.formatMessage({ id: 'toolbar.showAngles' })}
          </ContextToggleButton>
        )}
      </ContextButtonContainer>
    );
  };

  return (
    <>
      <ContextMessageContainer>
        <ContextMessage>{contextMessageText()}</ContextMessage>
      </ContextMessageContainer>
      {renderContextButton()}
      <Toolbar id="toolbar">
        <Flex $gap={Sizing.sm} $flexDirection="column">
          <Button
            id="toolbar_move"
            $isActive={isPanning}
            data-testid="hand-btn"
            onClick={() => {
              setSelectedElements([]);
              setSelectionMode({
                type: SelectionModes.Panning,
                elementType: undefined,
              });
            }}
          >
            <HandIcon
              color={isPanning ? ACTIVE_COLOR : INACTIVE_COLOR}
              isFill={isPanning}
            />
            <Text
              $fontSize={FontSize.sm}
              $textAlign="center"
              $color={isPanning ? activeColor : inactiveColor}
            >
              {intl.formatMessage({ id: 'toolbar.pan' })}
            </Text>
          </Button>
          <Button
            id="toolbar_select"
            $isActive={isActiveSelect}
            data-testid="pointer-btn"
            onClick={() => {
              setSelectedElements([]);
              setSelectionMode({
                type: SelectionModes.None,
                elementType: undefined,
              });
            }}
          >
            <PointerSvg
              color={isActiveSelect ? ACTIVE_COLOR : INACTIVE_COLOR}
              isFill={isActiveSelect}
            />
            <Text
              $fontSize={FontSize.sm}
              $textAlign="center"
              $color={isActiveSelect ? activeColor : inactiveColor}
            >
              {intl.formatMessage({ id: 'toolbar.select' })}
            </Text>
          </Button>
          <Button
            id="toolbar_text"
            $isActive={isAddingText}
            data-testid="text-btn"
            onClick={() => {
              setSelectedElements([]);
              setSelectionMode({
                type: SelectionModes.Add,
                elementType: ElementType.Text,
              });
            }}
          >
            <TextIcon
              color={isAddingText ? ACTIVE_COLOR : INACTIVE_COLOR}
              isFill={isAddingText}
            />
            <Text
              $fontSize={FontSize.sm}
              $textAlign="center"
              $color={isAddingText ? activeColor : inactiveColor}
            >
              {intl.formatMessage({ id: 'toolbar.text' })}
            </Text>
          </Button>
          <Button
            id="toolbar_polyline"
            $isActive={isAddingPolyline}
            data-testid="polyline-btn"
            onClick={() => {
              setSelectedElements([]);
              setSelectionMode({
                type: SelectionModes.Add,
                elementType: ElementType.Polyline,
                lineType: LineType.Line,
              });
            }}
          >
            <LineSvg
              color={isAddingPolyline ? ACTIVE_COLOR : INACTIVE_COLOR}
              isFill={isAddingPolyline}
            />
            <Text
              $fontSize={FontSize.sm}
              $textAlign="center"
              $color={isAddingPolyline ? activeColor : inactiveColor}
            >
              {intl.formatMessage({ id: 'toolbar.line' })}
            </Text>
          </Button>
          <Button
            id="toolbar_rect"
            $isActive={isAddingRect}
            data-testid="rect-btn"
            onClick={() => {
              setSelectedElements([]);
              setSelectionMode({
                type: SelectionModes.Add,
                elementType: ElementType.Rect,
              });
            }}
          >
            <RectSvg
              color={isAddingRect ? ACTIVE_COLOR : INACTIVE_COLOR}
              isFill={isAddingRect}
            />
            <Text
              $fontSize={FontSize.sm}
              $textAlign="center"
              $color={isAddingRect ? activeColor : inactiveColor}
            >
              {intl.formatMessage({ id: 'toolbar.rect' })}
            </Text>
          </Button>
          <Button
            id="toolbar_dimension"
            $isActive={isActiveDimension}
            data-testid="outer-dimension-btn"
            onClick={() => {
              setSelectedElements([]);
              setSelectionMode({
                type: SelectionModes.Add,
                elementType: ElementType.Polyline,
                lineType: LineType.Dimension,
              });
            }}
          >
            <Dimension
              color={isActiveDimension ? ACTIVE_COLOR : INACTIVE_COLOR}
              isFill={isActiveDimension}
            />
            <Text
              $fontSize={FontSize.sm}
              $textAlign="center"
              $color={isActiveDimension ? activeColor : inactiveColor}
            >
              {intl.formatMessage({ id: 'toolbar.dimension' })}
            </Text>
          </Button>
          <BottomDropdown values={symbolValues} />
        </Flex>
      </Toolbar>
    </>
  );
};

export default ToolbarComponent;
